import {
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import templateStyles from "./Templates.styles";
import strings from "global/constants/StringConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { useEffect, useRef, useState } from "react";
import SearchIcon from "assets/icons1/search_icon.svg";
import { templateMenu, templateOptions } from "./TemplateConstants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Design_Template from "assets/images/design_template.svg";
import { HandleTemplateType } from "models/interfaces";
import {
  addCampaignTemplate,
  deleteCampaignTemplate,
  getCampaignTemplates,
  getTemplateCount,
  updateCampaignTemplate,
  updateCampaignTemplatePermission,
} from "./TemplatesService";
import PulicTemp_SVG from "assets/images/public_template_svg.svg";
import system_svg from "assets/images/System_Template.svg";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { CustomInput } from "global/components";
import ConfirmationModal from "./Components/ConfirmationModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import urls from "global/constants/UrlConstants";
import UserTemplateModal from "./Components/UserTemplateModal";
import PreviewTemplateModal from "./Components/PreviewTemplateModal";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: "auto",
    },
  },
};

const Templates = () => {
  useTitle(title.TEMPLATES);
  const theme = useTheme();
  const classes = templateStyles(theme);
  const [selectedDropdownValue, setSelectedDropdownValue] =
    useState<string>("all");
  const [templatesFilters, setTemplatesFilters] = useState<{
    initialLoadCompleted: boolean;
    isLastElementInView: boolean;
    displayTemplates: HandleTemplateType[];
  }>({
    initialLoadCompleted: false,
    isLastElementInView: false,
    displayTemplates: [],
  });
  const [searchState, setSearchState] = useState<{
    page: number;
    searchText: string;
  }>({
    page: 1,
    searchText: "",
  });
  const [count, setCount] = useState<number>(0);
  const [perPageTemplates] = useState<number>(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPublicTemplate, setIsPublicTemplate] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openDuplicateModel, setOpenDuplicateModel] = useState<boolean>(false);
  const [openRenameModel, setOpenRenameModel] = useState<boolean>(false);
  const [openPreviewModel, setOpenPreviewModel] = useState<boolean>(false);
  const [openTemplateNameModel, setOpenTemplateNameModel] =
    useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isApiLoading, setApiLoading] = useState<boolean>(false);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const [formField, setFormField] = useState<any>({
    templateName: {
      value: "",
    },
    category: {
      value: "Private",
    },
  });

  useEffect(() => {
    fetchCampaignTemplates();
  }, [searchState, selectedDropdownValue]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !loading &&
          templatesFilters.displayTemplates.length < count
        ) {
          const maxPages = Math.ceil(count / perPageTemplates);
          if (searchState.page < maxPages) {
            setSearchState((prev) => ({ ...prev, page: prev.page + 1 }));
          }
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [
    loading,
    templatesFilters.displayTemplates.length,
    count,
    searchState.page,
  ]);

  const handleDropDownChange = (event: any) => {
    setSelectedDropdownValue(event.target.value);
    setTemplatesFilters((prevState) => ({
      ...prevState,
      initialLoadCompleted: false,
      isLastElementInView: false,
      displayTemplates: [],
    }));
    setSearchState({
      ...searchState,
      page: 1,
    });
  };

  const handleSearchOnChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchText = event.target.value.trim();
    if (searchState.searchText !== newSearchText) {
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        isLastElementInView: false,
        initialLoadCompleted: false,
      });
      setSearchState({
        page: 1,
        searchText: newSearchText,
      });
    }
  };

  const handleOpenMenu = (event: any, isPublic: boolean, template: any) => {
    setSelectedTemplate(template);
    setIsPublicTemplate(isPublic);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const fetchCampaignTemplates = async () => {
    try {
      setLoading(true);
      const countResponse = await getTemplateCount(
        selectedDropdownValue,
        searchState.searchText
      );

      const templatesResponse = await getCampaignTemplates(
        selectedDropdownValue,
        perPageTemplates,
        searchState.page,
        searchState.searchText
      );

      setCount(countResponse);
      setTemplatesFilters((prevFilters) => ({
        ...prevFilters,
        initialLoadCompleted: true,
        displayTemplates: [
          ...prevFilters.displayTemplates,
          ...templatesResponse,
        ],
      }));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const renderSkeletons = (forCreateTemplate: boolean) => {
    return Array.from({ length: forCreateTemplate ? 1 : 7 }).map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={`skeleton-${index}`}>
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{ width: "100%", height: 270 }}
        />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </Grid>
    ));
  };

  const handleDesignTemplate = () => {
    setOpenTemplateNameModel(true);
  };

  const handleViewOrEditTemplate = (templateId: number) => {
    history.push(`${viewpaths.templateDesignViewPath}?id=${templateId}`);
  };

  const handleDuplicateTemplate = () => {
    setOpenDuplicateModel(true);
  };

  const handlePreviewTemplate = () => {
    setOpenPreviewModel(true);
  };

  const handleRenameTemplate = () => {
    setFormField({
      ...formField,
      templateName: {
        value: selectedTemplate.name ? selectedTemplate.name : "",
      },
    });
    setOpenRenameModel(true);
  };

  const handlePermission = async (isPublic: boolean) => {
    let category: string;
    if (!isPublic) {
      category = "Public";
    } else {
      category = "Private";
    }
    try {
      setApiLoading(true);
      setAnchorEl(null);
      await updateCampaignTemplatePermission(selectedTemplate.id, category);
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        initialLoadCompleted: false,
        isLastElementInView: false,
      });
      setSearchState({
        ...searchState,
        page: 1,
      });
      openSuccessNotification(
        `Template visibility has been changed to ${
          !isPublic ? "Public" : "Private"
        }!`
      );
      setApiLoading(false);
    } catch (error: any) {
      setApiLoading(false);
      handleErrorMessage(error);
    }
  };

  const deleteTemplate = async (templateId: any) => {
    try {
      setApiLoading(true);
      const response = await deleteCampaignTemplate(templateId);
      openSuccessNotification(response.message);
      setOpenDeleteModal(false);
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        initialLoadCompleted: false,
        isLastElementInView: false,
      });
      setSearchState({
        ...searchState,
        page: 1,
      });
      setApiLoading(false);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setApiLoading(false);
    }
  };

  const handleMenuItemClick = async (action: string) => {
    switch (action) {
      case "campaign":
        // Handle Create Campaign
        break;
      case "preview":
        setAnchorEl(null);
        handlePreviewTemplate();
        break;
      case "edit":
        handleViewOrEditTemplate(selectedTemplate.id);
        break;
      case "duplicate":
        setAnchorEl(null);
        handleDuplicateTemplate();
        break;
      case "rename":
        setAnchorEl(null);
        handleRenameTemplate();
        break;
      case "exportHtml":
        break;
      case "visibility":
        setAnchorEl(null);
        await handlePermission(isPublicTemplate);
        break;
      case "delete":
        setAnchorEl(null);
        setOpenDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const TemplatesPage = () => {
    return (
      <Stack>
        <Grid container spacing={3} alignItems={"flex-end"}>
          {searchState.page === 1 && loading ? (
            renderSkeletons(true)
          ) : (
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={classes.addTemplateCardStyle}
                onClick={() => handleDesignTemplate()}
              >
                <img
                  src={Design_Template}
                  style={{
                    height: "60%",
                    margin: 2,
                  }}
                  alt="design template"
                />
                <Typography variant="h4" color="text.primary" pt={3}>
                  Design Template
                </Typography>
              </Card>
            </Grid>
          )}
          {templatesFilters.displayTemplates.map(
            (template: any, index: number) => {
              let isPublic = template.category === "Public";
              return (
                <Grid item xs={12} sm={6} md={3} key={template.id}>
                  <Box position="relative">
                    {template.category === "System" && (
                      <Box sx={classes.ribbon}>
                        <img src={system_svg} />
                      </Box>
                    )}
                    {template.category === "Public" && (
                      <Box sx={classes.ribbon}>
                        <img src={PulicTemp_SVG} />
                      </Box>
                    )}
                    <Card
                      sx={classes.templateCardStyle}
                      onClick={() => {
                        template.category !== "System" &&
                          handleViewOrEditTemplate(template.id);
                      }}
                    >
                      <Box
                        component="img"
                        src={template.image}
                        alt={template.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      <Box sx={classes.nameBox}>
                        <Typography
                          variant="subtitle1"
                          sx={classes.templateNameText}
                        >
                          {template.name}
                        </Typography>

                        <IconButton
                          size="small"
                          sx={{
                            color: "#fff",
                            padding: 0,
                          }}
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setFormField({
                              templateName: {
                                value: "",
                                error: "",
                              },
                              category: {
                                value: "Private",
                              },
                            });
                            handleOpenMenu(e, isPublic ?? false, template);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Box>
                </Grid>
              );
            }
          )}
          {loading && renderSkeletons(false)}
          <div ref={observerRef}></div>
        </Grid>
      </Stack>
    );
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <CustomInput
            id="templates_search_box"
            placeHolder="Search Templates"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            onChange={debounceEventHandler(
              handleSearchOnChange,
              strings.SEARCH_TIME_OUT
            )}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
            disabled={loading}
          />
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Select
              variant="outlined"
              id="templates_category_dropdown"
              value={selectedDropdownValue}
              onChange={handleDropDownChange}
              sx={classes.dropDown}
              MenuProps={MenuProps}
              disabled={loading}
            >
              {templateOptions.map((data, index: number) => (
                <MenuItem
                  key={index}
                  value={data.value}
                  sx={{
                    ...classes.dropDownMenuItem,
                    padding: "12px",
                  }}
                >
                  {data.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getTemplatesPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5" color="text.primary">
            Templates
          </Typography>
        </Stack>
        {getFilters()}
        {TemplatesPage()}
      </Stack>
    );
  };

  const handleUpdateTemplateName = async (selectedTemplate: any) => {
    try {
      if (selectedTemplate.name === formField.templateName.value.trim()) {
        return openErrorNotification(
          `A template with the name ${selectedTemplate.name} already exists. Please choose a different name. `
        );
      }
      setApiLoading(true);
      const template: any = {
        category: selectedTemplate.category,
        name: formField.templateName.value.trim(),
        jsonContent: selectedTemplate.jsonContent,
        customTools: [urls.customSocialTool, urls.customVideoTool],
        id: selectedTemplate.id ? selectedTemplate.id : undefined,
      };
      if (template.name && selectedTemplate.id) {
        const response = await updateCampaignTemplate(template);
        openSuccessNotification(response.message);
      }
      setFormField({
        templateName: {
          value: "",
          error: "",
        },
        category: {
          value: "Private",
        },
      });
      setAnchorEl(null);
      setSelectedDropdownValue("all");
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        initialLoadCompleted: false,
        isLastElementInView: false,
      });
      setSearchState({
        ...searchState,
        page: 1,
      });
      setApiLoading(false);
      setOpenRenameModel(false);
    } catch (error: any) {
      setOpenRenameModel(false);
      setApiLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleSaveTempName = async (selectedTemplate: any) => {
    try {
      setApiLoading(true);
      const template: any = {
        category: formField.category.value,
        name: formField.templateName.value.trim(),
        jsonContent: selectedTemplate.jsonContent,
        customTools: [urls.customSocialTool, urls.customVideoTool],
      };
      if (template.name) {
        const response = await addCampaignTemplate(template);
        openSuccessNotification(response.message);
      }
      setAnchorEl(null);
      setSelectedDropdownValue("all");
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        initialLoadCompleted: false,
        isLastElementInView: false,
      });
      setSearchState({
        ...searchState,
        page: 1,
      });
      setApiLoading(false);
      setOpenDuplicateModel(false);
    } catch (error: any) {
      setOpenDuplicateModel(false);
      setApiLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOnchange = (event: any) => {
    if (event.target.value !== undefined && event.target.value.length <= 40) {
      setFormField({
        ...formField,
        [event.target.name]: {
          ...formField[event.target.name],
          value: event.target.value,
          error: "",
        },
      });
    }
  };

  return (
    <CenteredAuthenticatedLayout>
      {getTemplatesPage()}
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {templateMenu({ isPublicTemplate, selectedTemplate }).map(
          (item, index) => (
            <MenuItem
              key={index}
              value={item.value}
              sx={classes.dropDownMenuItem}
              onClick={() => handleMenuItemClick(item.value)}
            >
              {item.label}
            </MenuItem>
          )
        )}
      </Menu>
      {openDeleteModal && (
        <ConfirmationModal
          isVisible={openDeleteModal}
          handleDialogClose={() => setOpenDeleteModal(false)}
          title="Please Confirm"
          subtitle2={
            <Typography>
              {`${"Do you really want to remove "}`}
              <span style={{ fontWeight: 700 }}>{selectedTemplate.name}?</span>
            </Typography>
          }
          btn1Variant="outlined"
          btn1Title="Cancel"
          btn2Title="Yes"
          btn1OnClick={() => setOpenDeleteModal(false)}
          btn2OnClick={() => deleteTemplate(selectedTemplate.id)}
          btn2Variant="contained"
        />
      )}
      {openTemplateNameModel && (
        <UserTemplateModal
          isVisible={openTemplateNameModel}
          handleDialogClose={() => setOpenTemplateNameModel(false)}
          title={"Enter template name"}
          id="templates_name_field"
          value={formField.templateName.value}
          name={"templateName"}
          onChange={(event: any) => handleOnchange(event)}
          lengthOfValue={formField.templateName?.value?.length}
          btn1Title="Cancel"
          btn1OnClick={() => {
            setAnchorEl(null);
            setOpenTemplateNameModel(false);
          }}
          btn2Title="Save"
          btn2OnClick={() =>
            history.push({
              pathname: `${viewpaths.templateDesignViewPath}`,
              state: {
                templateName: formField.templateName.value,
              },
            })
          }
        />
      )}
      {openDuplicateModel && (
        <UserTemplateModal
          isVisible={openDuplicateModel}
          handleDialogClose={() => setOpenDuplicateModel(false)}
          title={"Enter template name"}
          id="templates_name_field"
          value={formField.templateName.value}
          name={"templateName"}
          onChange={(event: any) => handleOnchange(event)}
          lengthOfValue={formField.templateName.value.length}
          btn1Title="Cancel"
          btn1OnClick={() => {
            setAnchorEl(null);
            setOpenDuplicateModel(false);
          }}
          btn2Title="Save"
          btn2OnClick={() => handleSaveTempName(selectedTemplate)}
        />
      )}
      {openRenameModel && (
        <UserTemplateModal
          isVisible={openRenameModel}
          handleDialogClose={() => setOpenRenameModel(false)}
          title={"Update template name"}
          id="templates_name_field"
          value={formField.templateName.value}
          name={"templateName"}
          onChange={(event: any) => handleOnchange(event)}
          lengthOfValue={selectedTemplate.name.length}
          btn1Title="Cancel"
          btn1OnClick={() => {
            setAnchorEl(null);
            setOpenRenameModel(false);
          }}
          btn2Title="Update"
          btn2OnClick={() => handleUpdateTemplateName(selectedTemplate)}
        />
      )}
      {openPreviewModel && (
        <PreviewTemplateModal
          isVisible={openPreviewModel}
          handleDialogClose={() => setOpenPreviewModel(false)}
          title={selectedTemplate.name ? selectedTemplate.name : ""}
          imageTemplate={selectedTemplate.image}
          btn1Title="Cancel"
          btn1OnClick={() => {
            setAnchorEl(null);
            setOpenPreviewModel(false);
          }}
        />
      )}
      <CustomLoader isLoading={isApiLoading} />
    </CenteredAuthenticatedLayout>
  );
};

export default Templates;
